import { PROJECTS } from "./data/data";

/**
 * @typedef {Object} RankingRow - one row as the aggregator returns it
 * @property {number} rank - DENSE_RANK, so no gaps
 * @property {string} project_slug - matches a project `id` in the catalog
 * @property {number} votes
 */

/**
 * @typedef {Object} RankingEntry
 * @property {number} rank
 * @property {number} votes
 * @property {import("./data/data").Project} project
 */

/**
 * Merge the aggregator's ranking with the project catalog.
 *
 * The aggregator only knows the projects that were voted on, the project is identified
 * by project_slug and corresponds to the id for the project in data.js.
 *
 * All the projects that are not returned by aggregator are not voted on, so have 0 votes.
 * These should be appended at the end in alphabetical order.
 *
 * @param {RankingRow[]} ranking
 * @param {import("./data/data").Project[]} [projects]
 * @returns {RankingEntry[]} ordered for display; empty when nobody has voted
 */
export function buildRanking(ranking, projects = PROJECTS) {
  const byId = new Map(projects.map((project) => [project.id, project]));
  const bySlug = (a, b) => a.project.id.localeCompare(b.project.id);

  // The aggregator already returns its rows by rank, ties alphabetically by slug
  const ranked = ranking
    // filter only projects known in data.js - catalog
    .filter((row) => byId.has(row.project_slug))
    .map(({ rank, votes, project_slug: slug }) => ({
      rank,
      votes,
      project: byId.get(slug),
    }));

  // No votes yet for projects defined in data.js - catalog
  if (ranked.length === 0) return [];

  const votedIds = new Set(ranked.map((entry) => entry.project.id));

  // Sort alphabetically the projects without votes and add them as the lowest rank (highest rank number)
  const unchosen = projects
    .filter((project) => !votedIds.has(project.id))
    .map((project) => ({
      rank: ranked.at(-1).rank + 1,
      votes: 0,
      project,
    }))
    .sort(bySlug);

  return [...ranked, ...unchosen];
}
